const DEFAULT_CALLBACK = '/'
const AUTH_ROUTE_PREFIX = '/auth/'

/** Strip origin from absolute URLs; return pathname + search only. */
export function toRelativeCallbackPath(value: string | undefined | null): string {
  if (!value || typeof value !== 'string') return DEFAULT_CALLBACK
  if (!value.startsWith('http://') && !value.startsWith('https://')) {
    return value.startsWith('/') ? value : `/${value}`
  }

  try {
    const parsed = new URL(value)
    return `${parsed.pathname}${parsed.search}` || DEFAULT_CALLBACK
  } catch {
    return DEFAULT_CALLBACK
  }
}

/** Build `/auth/{segment}` path. Use for non-standard segments (forgot-password, confirmation…). */
export function tenantAuthPath(segment: string): string {
  return `/auth/${segment.replace(/^\//, '')}`
}

function buildAuthPath(
  segment: string,
  params?: { callbackUrl?: string; reauth?: boolean; error?: string; email?: string }
): string {
  const p = new URLSearchParams()
  if (params?.callbackUrl) {
    p.set('callbackUrl', toRelativeCallbackPath(params.callbackUrl))
  }
  if (params?.reauth) p.set('reauth', '1')
  if (params?.error) p.set('error', params.error)
  if (params?.email) p.set('email', params.email)
  const qs = p.toString()
  const path = tenantAuthPath(segment)
  return qs ? `${path}?${qs}` : path
}

/** Post-login destinations must not point back into auth bootstrap routes. */
export function sanitizeAuthReturnTo(value: string | undefined | null): string {
  const normalized = toRelativeCallbackPath(value)
  if (
    normalized.startsWith(AUTH_ROUTE_PREFIX) ||
    normalized === '/sign-out' ||
    normalized.startsWith('/sem-evento')
  ) {
    return DEFAULT_CALLBACK
  }
  return normalized
}

/** Auth route builders. */
export const authPaths = {
  signIn(params?: {
    callbackUrl?: string
    reauth?: boolean
    error?: string
  }): string {
    return buildAuthPath('sign-in', params)
  },
  signUp(params?: { callbackUrl?: string }): string {
    return buildAuthPath('sign-up', params)
  },
  forgotPassword(): string {
    return tenantAuthPath('forgot-password')
  },
  resetPassword(): string {
    return tenantAuthPath('reset-password')
  },
  confirmation(params?: { email?: string }): string {
    return buildAuthPath('confirmation', params)
  },
  resendConfirmation(): string {
    return tenantAuthPath('resend-confirmation')
  }
}
